import React, { useState } from 'react'
import { Col, Form } from 'react-bootstrap'
import { CustomAnswer, CustomQuestion, CustomQuestionType } from '../../store/events/interfaces'
import { filterCustomQuestions } from '../../utils/customQuestions'
import {
  FieldError,
  FieldValues,
  FormState,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useAppSelector } from '../../hooks'

const SelectCustomQuestion = (props: {
  question: CustomQuestion
  register: UseFormRegister<FieldValues>
  loadedViaCode: boolean
  loadedViaFamilyToken: boolean
  answeredQuestions: number[]
  errors: Record<string, any>
  formState: FormState<FieldValues>
  defaultValue: (question: CustomQuestion) => string
}) => {
  const {
    question,
    register,
    errors,
    loadedViaCode,
    loadedViaFamilyToken,
    answeredQuestions,
    formState,
    defaultValue
  } = props
  const value = defaultValue(question)
  const [isHighlighted, setIsHighlighted] = useState(loadedViaFamilyToken)

  return (
    <>
      <Form.Control
        {...register(`custom_answer$${question.id}`, {
          onBlur: () => setIsHighlighted(false),
          onChange: () => setIsHighlighted(false)
        })}
        className={`m-0 ${isHighlighted ? 'highlight-animation' : ''}`}
        required={question.required}
        as='select'
        defaultValue={value}
        disabled={
          loadedViaCode &&
          answeredQuestions.includes(question.id)
        }
        isValid={
          formState.touchedFields[`custom_answer$${question.id}`] &&
          !errors[`custom_answer$${question.id}`]
        }
        isInvalid={!!errors[`custom_answer$${question.id}`]}
      >
        {question.type === CustomQuestionType.YESNO ? (
          <>
            <option value='' />
            <option value='Ja'>Ja</option>
            <option value='Nein'>Nein</option>
          </>
        ) : (
          <>
            <option />
            {question.choices.map((choice: string) => (
              <option value={choice}
                key={`${question.id}_${question.choices.findIndex(
                  c => c === choice,
                )}`}
              >
                {choice}
              </option>
            ))}
          </>
        )}
      </Form.Control>
      <Form.Control.Feedback type='invalid'>
        {errors[`custom_answer$${question.id}`]
          ? (errors[`custom_answer$${question.id}`] as FieldError).message
          : ''}
      </Form.Control.Feedback>
    </>
  )
}

function TextCustomQuestion(props: {
  register: UseFormRegister<FieldValues>
  question: CustomQuestion
  defaultValue: string
  loadedViaCode: boolean
  loadedViaFamilyToken: boolean
  answeredQuestions: any
  formState: FormState<FieldValues>
  errors: Record<string, any>
}) {
  const {
    question,
    register,
    loadedViaCode,
    loadedViaFamilyToken,
    answeredQuestions,
    formState,
    defaultValue,
    errors
  } = props
  const [isHighlighted, setIsHighlighted] = useState(loadedViaFamilyToken)
  return (
    <>
      <Form.Control
        {...register(`custom_answer$${question.id}`, {
          onBlur: () => setIsHighlighted(false),
          onChange: () => setIsHighlighted(false)
        })}
        className={`m-0 ${isHighlighted ? 'highlight-animation' : ''}`}
        required={question.required}
        type="text"
        defaultValue={defaultValue}
        disabled={
          loadedViaCode &&
          answeredQuestions.includes(question.id)
        }
        isValid={
          formState.touchedFields[`custom_answer$${question.id}`] &&
          !errors[`custom_answer$${question.id}`]
        }
        isInvalid={!!errors[`custom_answer$${question.id}`]}
      />
      <Form.Control.Feedback type="invalid">
        {errors[`custom_answer$${question.id}`]
          ? (errors[`custom_answer$${question.id}`] as FieldError)
              .message
          : ''}
      </Form.Control.Feedback>
    </>
  )
}

export const ParticipantCustomQuestions = (props: {
  questions: CustomQuestion[]
  register: UseFormRegister<FieldValues>
  loadedViaCode: boolean
  setValue: UseFormSetValue<FieldValues>
  errors: Record<string, any>
  formState: FormState<FieldValues>
}) => {
  const { questions, register, errors, loadedViaCode, formState, setValue } = props
  const supportedQuestions = filterCustomQuestions(questions)
  const answeredCustomQuestions = useAppSelector(
    (state) => state.program.answeredParticipantCustomQuestions,
  )
  const participant = useAppSelector(
    (state) => state.participant.data,
  )
  const { loadedViaFamilyToken } = useAppSelector((state) => state.misc)

  const defaultValue = (question: CustomQuestion) => {
    return participant.custom_answers !== undefined &&
      participant.custom_answers.find(
        (q: CustomAnswer) => q.question_id === question.id
      ) !== undefined
      ? (
          participant.custom_answers.find(
            (q: CustomAnswer) => q.question_id === question.id
          ) as CustomAnswer
        ).answer_text
      : ''
  }

  if (loadedViaCode) {
    console.log(`Loaded via code, presetting values via`, supportedQuestions)
    supportedQuestions.forEach((question: CustomQuestion) => {
      const value = defaultValue(question)
      if (value) {
        setValue(`custom_answer$${question.id}`, defaultValue(question))
      }
    })
  }

  if (supportedQuestions.length === 0) {
    return <></>
  }
  return (
    <>
      <Form.Row>
        <h5>Weitere Daten</h5>
      </Form.Row>
      {supportedQuestions.map((question: CustomQuestion, index: number) => (
        <Form.Row key={`custom_answer$${question.id}`}>
          <Col>
            <Form.Group controlId={`custom_answer$${question.id}`}>
              <Form.Label>
                {question.label}
                {question.required ? '*' : ''}
              </Form.Label>
              {question.type !== CustomQuestionType.TEXT ? (
                <SelectCustomQuestion
                  question={question}
                  register={register}
                  errors={errors}
                  loadedViaCode={loadedViaCode}
                  loadedViaFamilyToken={loadedViaFamilyToken}
                  answeredQuestions={answeredCustomQuestions}
                  formState={formState}
                  defaultValue={defaultValue}
                />
              ) : (
                <TextCustomQuestion
                  register={register}
                  question={question}
                  defaultValue={defaultValue(question)}
                  loadedViaCode={loadedViaCode}
                  loadedViaFamilyToken={loadedViaFamilyToken}
                  answeredQuestions={answeredCustomQuestions}
                  formState={formState}
                  errors={errors}
                />
              )}
              <Form.Text className="text-muted">
                {question.description}
              </Form.Text>
            </Form.Group>
          </Col>
        </Form.Row>
      ))}
    </>
  )
}
